import { useState } from "react";
import { msgid, ngettext, t } from "ttag";

import { ConfirmModal } from "metabase/common/components/ConfirmModal";
import { useDashboardContext } from "metabase/dashboard/context/context";
import {
  getIsDirty,
  getMissingRequiredParameters,
} from "metabase/dashboard/selectors";
import { useRegisterShortcut } from "metabase/palette/hooks/useRegisterShortcut";
import { useDispatch, useSelector } from "metabase/redux";
import { dismissAllUndo } from "metabase/redux/undo";
import { Button, Tooltip } from "metabase/ui";
import type { UiParameter } from "metabase-lib/v1/parameters/types";

export const SaveEditButton = () => {
  const dispatch = useDispatch();
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const {
    setEditingDashboard,
    updateDashboardAndCards,
    onRefreshPeriodChange,
  } = useDashboardContext();

  const isDirty = useSelector(getIsDirty);
  const missingRequiredParameters = useSelector(getMissingRequiredParameters);

  const disabledSaveTooltip = getDisabledSaveButtonTooltip(
    missingRequiredParameters,
  );
  const isSaveDisabled = missingRequiredParameters.length > 0;

  const handleDoneEditing = () => {
    onRefreshPeriodChange(null);
    setEditingDashboard(null);
  };

  const onSave = async () => {
    // optimistically dismissing all the undos before the saving has finished
    // clicking on them wouldn't do anything at this moment anyway
    dispatch(dismissAllUndo());
    await updateDashboardAndCards();

    handleDoneEditing();
  };

  const handleSaveClick = () => {
    if (isSaveDisabled) {
      return;
    }
    if (isDirty) {
      setShowSaveConfirm(true);
    } else {
      onSave();
    }
  };

  const handleConfirmSave = async () => {
    await onSave();
    setShowSaveConfirm(false);
  };

  useRegisterShortcut([
    {
      id: "dashboard-save",
      perform: handleSaveClick,
    },
  ]);

  return (
    <>
      <Tooltip
        key="save"
        label={disabledSaveTooltip}
        disabled={!isSaveDisabled}
      >
        <span>
          <Button
            variant="filled"
            size="sm"
            onClick={handleSaveClick}
            disabled={isSaveDisabled}
            data-testid="save-edit-button"
          >
            {t`Save`}
          </Button>
        </span>
      </Tooltip>
      <ConfirmModal
        opened={showSaveConfirm}
        title={t`Save dashboard`}
        message={t`Would you like to save the changes before proceeding?`}
        confirmButtonText={t`Save`}
        confirmButtonProps={{ color: "brand" }}
        closeButtonText={t`Cancel`}
        onClose={() => setShowSaveConfirm(false)}
        onConfirm={handleConfirmSave}
        data-testid="save-dashboard-confirm-modal"
      />
    </>
  );
};

function getDisabledSaveButtonTooltip(
  missingRequiredParams: UiParameter[],
): string {
  if (!missingRequiredParams.length) {
    return "";
  }

  const names = missingRequiredParams
    .map((param) => `"${param.name}"`)
    .join(", ");

  return ngettext(
    msgid`The ${names} parameter requires a default value but none was provided.`,
    `The ${names} parameters require default values but none were provided.`,
    missingRequiredParams.length,
  );
}
